import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import classNames from "classnames";
import React from "react";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { handleKeyDown } from "@faire/retailer/lib/handleKeyDown";
import * as animatedStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/animation.css";
import { useAnimatedScroll } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/useAnimatedScroll";
import { invisible } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Prerender/style.css";
import * as tabStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Tabs/tabs.css";
import { BrandValueBannerBrandInfo } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/BrandValueBannerBrandInfo";
import { useGetBrandValues } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/useGetBrandValues";
import * as componentStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/styles.css";

export const BrandValueBanner: React.FC = () => {
  const getBrandValues = useGetBrandValues();
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView("brand_value_banner")
  );
  const {
    resumeScroll,
    pauseScroll,
    isVisible,
    currentIndex,
    setCurrentIndex,
  } = useAnimatedScroll(getBrandValues?.length ?? 0, 5000);

  const handleClickTab = (index: number, brandValue: string) => {
    pauseScroll();
    setCurrentIndex(index);
    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
      "brand_value_banner-tab",
      brandValue
    );
    setTimeoutIfPossible(() => {
      resumeScroll();
    }, 12000);
  };

  return (
    <Column
      gap="2x"
      className={classNames(
        componentStyles.wrapper,
        animatedStyles.animatedContainerParent
      )}
      ref={ref}
    >
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          defaultMessage="Shop by the values you care most about"
          description={{
            text: "brand value banner header",
            img: "https://cdn.faire.com/fastly/1bb1746876e68497e743ddaa9806c3a4bf9b152cc5023da2ca04ec028d61f734.png",
          }}
        />
      </Typography>

      <div className={tabStyles.tabBar} style={{ maxWidth: "866px" }}>
        {getBrandValues.map((brandValue, index) => (
          <Typography
            tabIndex={0}
            data-is-selected={index === currentIndex}
            className={tabStyles.tab}
            key={index}
            onClick={() => handleClickTab(index, brandValue.id)}
            onKeyDown={handleKeyDown(() =>
              handleClickTab(index, brandValue.id)
            )}
          >
            {brandValue.name}
          </Typography>
        ))}
      </div>
      <div
        style={{ overflow: "hidden" }}
        onMouseEnter={pauseScroll}
        onMouseLeave={resumeScroll}
        className={classNames(
          animatedStyles.animatedContainer,
          animatedStyles.animatedContainerParent
        )}
        data-is-visible={isVisible}
      >
        {isPrerender() ? (
          <>
            {/* we want to prerender all brand values */}
            {getBrandValues.map((brandValue, index) => (
              <BrandValueBannerBrandInfo
                key={index}
                brandInfo={brandValue.brand}
                className={index ? invisible : ""}
              />
            ))}
          </>
        ) : (
          <BrandValueBannerBrandInfo
            brandInfo={getBrandValues[currentIndex]?.brand}
          />
        )}
      </div>
    </Column>
  );
};
